import { useState } from 'react';
import { PopoverManagerContext } from './';
import { jsx as _jsx } from "react/jsx-runtime";
const findFirstAvailableId = ids => {
  ids.sort();
  let largestId = -1;
  for (let i = 0; i < ids.length; i++) {
    if (ids[i] > largestId) {
      largestId = ids[i];
    }
    // check if id i is already taken
    if (ids[i] !== i) {
      // id i is available so use it
      return i;
    }
  }
  // There weren't any unused intermediate ids so just increment one more than largest id
  return largestId + 1;
};

/**
 * This is used internally by AppRoot. Consumers should not need to use this in isolation.
 */
export const PopoverManagerProvider = props => {
  const [activePopovers, setActivePopovers] = useState(new Map());
  return /*#__PURE__*/_jsx(PopoverManagerContext.Provider, {
    value: {
      activePopovers,
      openPopover: referenceDetails => {
        let id = -1;

        // Sometimes popovers are opened simultaneously, need to look at previous values to be sure everything is up to date
        setActivePopovers(prev => {
          // When popover opens check if it is a child of an existing popover and apply it a group and level
          const existingPopovers = Array.from(prev.values());
          const existingIds = existingPopovers.map(popover => {
            return popover.id;
          });
          id = findFirstAvailableId(existingIds);
          let highestGroup = -1;
          let updateGroup = -1;

          // See if this popover came from inside another one
          for (const popover of existingPopovers) {
            const group = popover.group;
            if (referenceDetails.referenceElementRef?.current && popover.popoverElement.contains(referenceDetails.referenceElementRef?.current)) {
              // This popover came from the one we're iterating over, keep the same group, increment the level
              const level = popover.level + 1;

              // A popover can only have one parent so none of the other popovers should need updating, return early
              prev.set(id, {
                popoverElement: referenceDetails.popoverElement,
                id,
                group,
                level,
                referenceElementRef: referenceDetails.referenceElementRef
              });
              return new Map(prev);
            } else if (popover.referenceElementRef?.current && referenceDetails.popoverElement.contains(popover.referenceElementRef?.current)) {
              // This popover is a parent of the one we're iterating over
              if (updateGroup === -1) {
                // If this is the first popover that should be a child of this one, use the same group as child, but update levels of all children
                updateGroup = popover.group;
                // Find all children
                const children = existingPopovers.filter(childPopover => {
                  return childPopover.group === updateGroup;
                });
                children.forEach(child => {
                  child.level++;
                  prev.set(id, child);
                });
              } else {
                // We have already found a different child and used its group, update group and levels of these children to match
                const children = existingPopovers.filter(childPopover => {
                  return childPopover.group === popover.group;
                });
                children.forEach(child => {
                  // These children should have the same group as the other group of popovers that was updated
                  child.group = updateGroup;
                  child.level++;
                  prev.set(id, child);
                });
              }
            } else if (group !== undefined && group > highestGroup) {
              highestGroup = group;
            }
          }

          // This code will be hit if a popover is getting added as a new group, or as a parent of other popovers
          // Use update group if its been changed, otherwise use the highest group
          const group = updateGroup === -1 ? highestGroup + 1 : updateGroup;
          const level = 0;
          prev.set(id, {
            popoverElement: referenceDetails.popoverElement,
            id,
            group,
            level,
            referenceElementRef: referenceDetails.referenceElementRef
          });
          return new Map(prev);
        });
        return id;
      },
      closePopover: id => {
        setActivePopovers(prev => {
          prev.delete(id);
          return new Map(prev);
        });
      }
    },
    children: props.children
  });
};
//# sourceMappingURL=PopoverManagerProvider.js.map